import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { sensorService } from '../services/sensorService';
import { SensorReading, Sensor } from '../types';
import SE0XReadingsChart from './SE0XReadingsChart';
import DDS45ReadingsChart from './DDS45ReadingsChart';
import LHT65NReadingsChart from './LHT65NReadingsChart';
import WQSPHReadingsChart from './WQSPHReadingsChart';
import WQSECReadingsChart from './WQSECReadingsChart';
import { getStoredTempUnit, convertTemperature, tempUnitLabel } from '../utils/temperatureUtils';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

interface ReadingsChartProps {
  sensorId: number;
  sensorType?: string;
  sensor?: Sensor | null;
  latestReading?: SensorReading | null;
  onNewReading?: (reading: SensorReading) => void;
}

type DateRangePreset = '1d' | '7d' | '30d' | '90d' | 'custom';

/** Check if a reading has SE0X-LB multi-probe data */
function hasSE0XData(reading: SensorReading): boolean {
  return (
    reading.temp_soil1 != null || reading.water_soil1 != null || reading.conduct_soil1 != null ||
    reading.temp_soil2 != null || reading.water_soil2 != null || reading.conduct_soil2 != null ||
    reading.temp_soil3 != null || reading.water_soil3 != null || reading.conduct_soil3 != null ||
    reading.temp_soil4 != null || reading.water_soil4 != null || reading.conduct_soil4 != null ||
    reading.probe_flags != null
  );
}

const ReadingsChart: React.FC<ReadingsChartProps> = ({ sensorId, sensorType, sensor, latestReading, onNewReading }) => {
  const { t } = useTranslation();
  const [readings, setReadings] = useState<SensorReading[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Toggle states for each line
  const [showTemperature, setShowTemperature] = useState(true);
  const [showMoisture, setShowMoisture] = useState(true);
  const [showEC, setShowEC] = useState(true);
  const [showLeafMoisture, setShowLeafMoisture] = useState(true);
  const [showLeafTemperature, setShowLeafTemperature] = useState(true);
  const [showBattery, setShowBattery] = useState(true);
  const [showPAR, setShowPAR] = useState(true);
  
  // Date range states
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('1d');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  
  // Polling ref
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastReadingIdRef = useRef<number | null>(null);
  const isPollingInProgressRef = useRef<boolean>(false);

  // Detect sensor type based on readings
  const isLeafSensor = readings.length > 0 && 
    (readings.some(r => r.leaf_moisture !== null) || readings.some(r => r.leaf_temperature !== null));
  
  // Detect SE0X-LB multi-probe sensor.
  // Three independent signals — any one is sufficient:
  //  1. sensor_type prop contains "SE0X" (updated by webhook from Node_type)
  //  2. Any already-loaded reading has probe data
  //  3. The live latestReading passed from the parent has probe data
  const isSE0XSensor = (
    (sensorType != null && sensorType.includes('SE0X')) ||
    readings.some(r => hasSE0XData(r)) ||
    (latestReading != null && hasSE0XData(latestReading))
  );

  // Detect DDS45-LB distance/level sensor
  const isDDS45Sensor = (
    (sensorType != null && sensorType.includes('DDS45')) ||
    readings.some(r => r.distance_mm != null) ||
    (latestReading != null && latestReading.distance_mm != null)
  );

  // Detect LHT65N/S temperature & humidity sensor
  const isLHT65NSensor = (
    (sensorType != null && sensorType.includes('LHT65')) ||
    readings.some(r => r.air_temperature != null || r.air_humidity != null) ||
    (latestReading != null && (latestReading.air_temperature != null || latestReading.air_humidity != null))
  );

  // Detect a WQS-LB converter carrying a DR-ECK1.0 EC probe.
  const isWQSECSensor = (
    (sensorType != null && sensorType.includes('DR-ECK1.0')) ||
    readings.some(r => r.ec_temperature != null) ||
    (latestReading != null && latestReading.ec_temperature != null)
  );

  // Detect a WQS-LB converter carrying a DR-PH01 probe.
  const isWQSPHSensor = (
    (sensorType != null && sensorType.includes('DR-PH01')) ||
    readings.some(r => r.ph_value != null || r.ph_temperature != null) ||
    (latestReading != null && (latestReading.ph_value != null || latestReading.ph_temperature != null))
  );

  // Detect a WSC2-L carrying a photosynthetically active radiation sensor.
  // `!= null` deliberately keeps zero as a valid nighttime measurement.
  const isPARSensor = (
    (sensorType != null && (sensorType.includes('WSC2') || sensorType.includes('PAR'))) ||
    readings.some(r => r.par != null) ||
    (latestReading != null && latestReading.par != null)
  );

  // Calculate date range based on preset
  const getDateRange = useCallback(() => {
    const end = new Date();
    let start = new Date();
    
    if (dateRangePreset === 'custom' && startDate && endDate) {
      return {
        start: new Date(startDate),
        end: new Date(endDate),
      };
    }
    
    switch (dateRangePreset) {
      case '1d':
        start.setDate(start.getDate() - 1);
        break;
      case '7d':
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        break;
      case '90d':
        start.setDate(start.getDate() - 90);
        break;
    }
    
    return { start, end };
  }, [dateRangePreset, startDate, endDate]);

  // Determine time unit based on date range
  const getTimeUnit = useCallback(() => {
    const { start, end } = getDateRange();
    const diffMs = end.getTime() - start.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    
    if (diffDays <= 1) return 'hour';
    if (diffDays <= 7) return 'day';
    if (diffDays <= 60) return 'day';
    return 'week';
  }, [getDateRange]);

  // Load readings
  const loadReadings = useCallback(async (isPolling = false) => {
    // Prevent overlapping polling requests
    if (isPolling && isPollingInProgressRef.current) {
      return;
    }
    
    if (isPolling) {
      isPollingInProgressRef.current = true;
    } else {
      setLoading(true);
    }
    
    try {
      const { start, end } = getDateRange();
      const data = await sensorService.getReadings(sensorId, {
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        limit: 5000,
      });
      
      // Sort by timestamp ascending for the chart
      const sortedData = data.sort((a, b) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      
      setReadings(sortedData);
      
      // Check for new readings during polling
      if (isPolling && sortedData.length > 0) {
        const newestReading = sortedData[sortedData.length - 1];
        if (lastReadingIdRef.current !== null && 
            newestReading.id !== lastReadingIdRef.current) {
          // New reading detected
          if (onNewReading) {
            onNewReading(newestReading);
          }
        }
        lastReadingIdRef.current = newestReading.id;
      } else if (!isPolling && sortedData.length > 0) {
        lastReadingIdRef.current = sortedData[sortedData.length - 1].id;
      }
      
      setError(null);
    } catch (err: any) {
      if (!isPolling) {
        setError(err.response?.data?.detail || 'Failed to load readings');
      }
    } finally {
      if (isPolling) {
        isPollingInProgressRef.current = false;
      } else {
        setLoading(false);
      }
    }
  }, [sensorId, getDateRange, onNewReading]);

  // Initial load and when date range changes
  useEffect(() => {
    loadReadings();
  }, [loadReadings]);

  // Set up polling for real-time updates
  useEffect(() => {
    // Poll every 30 seconds for new readings
    pollingIntervalRef.current = setInterval(() => {
      loadReadings(true);
    }, 30000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [loadReadings]);

  // Update chart when latestReading changes from parent
  useEffect(() => {
    if (latestReading) {
      setReadings(prev => {
        const exists = prev.some(r => r.id === latestReading.id);
        if (!exists) {
          const updated = [...prev, latestReading].sort((a, b) => 
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
          return updated;
        }
        return prev;
      });
    }
  }, [latestReading]);

  // Prepare chart data
  const tempUnit = getStoredTempUnit();
  const unitLabel = tempUnitLabel(tempUnit);

  const chartData = {
    labels: readings.map(r => new Date(r.timestamp)),
    datasets: [
      // Soil sensor datasets
      ...(!isLeafSensor && !isPARSensor && showTemperature ? [{
        label: `Temperature (${unitLabel})`,
        data: readings.map(r => convertTemperature(r.temperature, tempUnit)),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderWidth: 2,
        pointRadius: readings.length > 100 ? 0 : 3,
        tension: 0.1,
        yAxisID: 'y',
      }] : []),
      ...(!isLeafSensor && !isPARSensor && showMoisture ? [{
        label: 'Soil Moisture (%)',
        data: readings.map(r => r.soil_moisture),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.5)',
        borderWidth: 2,
        pointRadius: readings.length > 100 ? 0 : 3,
        tension: 0.1,
        yAxisID: 'y1',
      }] : []),
      ...(!isLeafSensor && !isPARSensor && showEC ? [{
        label: 'EC (mS/cm)',
        data: readings.map(r => r.electrical_conductivity != null ? r.electrical_conductivity / 1000 : null),
        borderColor: 'rgb(168, 85, 247)',
        backgroundColor: 'rgba(168, 85, 247, 0.5)',
        borderWidth: 2,
        pointRadius: readings.length > 100 ? 0 : 3,
        tension: 0.1,
        yAxisID: 'y2',
      }] : []),
      
      // Leaf sensor datasets
      ...(isLeafSensor && showLeafTemperature ? [{
        label: `Leaf Temperature (${unitLabel})`,
        data: readings.map(r => convertTemperature(r.leaf_temperature, tempUnit)),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderWidth: 2,
        pointRadius: readings.length > 100 ? 0 : 3,
        tension: 0.1,
        yAxisID: 'y',
      }] : []),
      ...(isLeafSensor && showLeafMoisture ? [{
        label: 'Leaf Moisture (%)',
        data: readings.map(r => r.leaf_moisture),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.5)',
        borderWidth: 2,
        pointRadius: readings.length > 100 ? 0 : 3,
        tension: 0.1,
        yAxisID: 'y1',
      }] : []),
      ...(isLeafSensor && showBattery ? [{
        label: 'Battery (V)',
        data: readings.map(r => r.battery_voltage),
        borderColor: 'rgb(251, 191, 36)',
        backgroundColor: 'rgba(251, 191, 36, 0.5)',
        borderWidth: 2,
        pointRadius: readings.length > 100 ? 0 : 3,
        tension: 0.1,
        yAxisID: 'y2',
      }] : []),
      ...(isPARSensor && showPAR ? [{
        label: t('par.unitLabel'),
        data: readings.map(r => r.par),
        borderColor: 'rgb(5, 150, 105)',
        backgroundColor: 'rgba(5, 150, 105, 0.5)',
        borderWidth: 2,
        pointRadius: readings.length > 100 ? 0 : 3,
        tension: 0.1,
        yAxisID: 'y',
      }] : []),
      ...(isPARSensor && showBattery ? [{
        label: `${t('dashboard.battery')} (V)`,
        data: readings.map(r => r.battery_voltage),
        borderColor: 'rgb(245, 158, 11)',
        backgroundColor: 'rgba(245, 158, 11, 0.5)',
        borderWidth: 2,
        pointRadius: readings.length > 100 ? 0 : 3,
        tension: 0.1,
        yAxisID: 'y1',
      }] : []),
    ],
  };

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: isPARSensor ? t('par.chartTitle') : 'Sensor Readings Over Time',
      },
      tooltip: {
        callbacks: {
          title: (items) => {
            if (items.length > 0 && items[0].parsed.x != null) {
              const date = new Date(items[0].parsed.x);
              return date.toLocaleString();
            }
            return '';
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: getTimeUnit(),
          displayFormats: {
            hour: 'HH:mm',
            day: 'MMM d',
            week: 'MMM d',
          },
        },
        title: {
          display: true,
          text: 'Time',
        },
      },
      y: {
        type: 'linear',
        display: isPARSensor ? showPAR : (isLeafSensor ? showLeafTemperature : showTemperature),
        position: 'left',
        title: {
          display: true,
          text: isPARSensor ? t('par.unitLabel') : (isLeafSensor ? `Leaf Temperature (${unitLabel})` : `Temperature (${unitLabel})`),
          color: isPARSensor ? 'rgb(5, 150, 105)' : 'rgb(59, 130, 246)',
        },
        ticks: {
          color: isPARSensor ? 'rgb(5, 150, 105)' : 'rgb(59, 130, 246)',
        },
      },
      y1: {
        type: 'linear',
        display: isPARSensor ? showBattery : (isLeafSensor ? showLeafMoisture : showMoisture),
        position: 'left',
        title: {
          display: true,
          text: isPARSensor ? `${t('dashboard.battery')} (V)` : (isLeafSensor ? 'Leaf Moisture (%)' : 'Soil Moisture (%)'),
          color: isPARSensor ? 'rgb(245, 158, 11)' : 'rgb(34, 197, 94)',
        },
        ticks: {
          color: isPARSensor ? 'rgb(245, 158, 11)' : 'rgb(34, 197, 94)',
        },
        grid: {
          drawOnChartArea: false,
        },
      },
      y2: {
        type: 'linear',
        display: !isPARSensor && (isLeafSensor ? showBattery : showEC),
        position: 'right',
        title: {
          display: true,
          text: isLeafSensor ? 'Battery (V)' : 'EC (mS/cm)',
          color: isLeafSensor ? 'rgb(251, 191, 36)' : 'rgb(168, 85, 247)',
        },
        ticks: {
          color: isLeafSensor ? 'rgb(251, 191, 36)' : 'rgb(168, 85, 247)',
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  const handlePresetChange = (preset: DateRangePreset) => {
    setDateRangePreset(preset);
    if (preset !== 'custom') {
      setStartDate('');
      setEndDate('');
    }
  };

  const handleCustomDateChange = () => {
    if (startDate && endDate) {
      setDateRangePreset('custom');
    }
  };

  // If WQS-LB + DR-ECK1.0 sensor, delegate to specialized component
  if (isWQSECSensor) {
    return (
      <WQSECReadingsChart
        sensorId={sensorId}
        latestReading={latestReading}
        onNewReading={onNewReading}
      />
    );
  }

  // If WQS-LB + DR-PH01 sensor, delegate to specialized component
  if (isWQSPHSensor) {
    return (
      <WQSPHReadingsChart
        sensorId={sensorId}
        latestReading={latestReading}
        onNewReading={onNewReading}
      />
    );
  }

  // If LHT65N/S sensor, delegate to specialized component
  if (isLHT65NSensor) {
    return (
      <LHT65NReadingsChart
        sensorId={sensorId}
        latestReading={latestReading}
        onNewReading={onNewReading}
      />
    );
  }

  // If DDS45-LB sensor, delegate to specialized component
  if (isDDS45Sensor) {
    return (
      <DDS45ReadingsChart
        sensorId={sensorId}
        sensor={sensor}
        latestReading={latestReading}
        onNewReading={onNewReading}
      />
    );
  }

  // If SE0X-LB sensor, delegate to specialized component
  if (isSE0XSensor) {
    return (
      <SE0XReadingsChart
        sensorId={sensorId}
        latestReading={latestReading}
        onNewReading={onNewReading}
      />
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="mb-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Toggle buttons */}
          <div className="flex flex-wrap gap-2">
            {isPARSensor ? (
              <>
                <button
                  onClick={() => setShowPAR(!showPAR)}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    showPAR
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-emerald-700 border-emerald-600 hover:bg-emerald-50'
                  }`}
                >
                  {t('par.par')}
                </button>
                <button
                  onClick={() => setShowBattery(!showBattery)}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    showBattery
                      ? 'bg-yellow-500 text-white border-yellow-500'
                      : 'bg-white text-yellow-600 border-yellow-500 hover:bg-yellow-50'
                  }`}
                >
                  {t('dashboard.battery')}
                </button>
              </>
            ) : isLeafSensor ? (
              <>
                <button
                  onClick={() => setShowLeafTemperature(!showLeafTemperature)}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    showLeafTemperature
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
                  }`}
                >
                  {t('dashboard.leafTemp')}
                </button>
                <button
                  onClick={() => setShowLeafMoisture(!showLeafMoisture)}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    showLeafMoisture
                      ? 'bg-green-500 text-white border-green-500'
                      : 'bg-white text-green-500 border-green-500 hover:bg-green-50'
                  }`}
                >
                  {t('dashboard.leafMoisture')}
                </button>
                <button
                  onClick={() => setShowBattery(!showBattery)}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    showBattery
                      ? 'bg-yellow-500 text-white border-yellow-500'
                      : 'bg-white text-yellow-500 border-yellow-500 hover:bg-yellow-50'
                  }`}
                >
                  {t('dashboard.battery')}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setShowTemperature(!showTemperature)}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    showTemperature
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
                  }`}
                >
                  {t('dashboard.temperature')}
                </button>
                <button
                  onClick={() => setShowMoisture(!showMoisture)}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    showMoisture
                      ? 'bg-green-500 text-white border-green-500'
                      : 'bg-white text-green-500 border-green-500 hover:bg-green-50'
                  }`}
                >
                  {t('sensorDetail.moisture')}
                </button>
                <button
                  onClick={() => setShowEC(!showEC)}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    showEC
                      ? 'bg-purple-500 text-white border-purple-500'
                      : 'bg-white text-purple-500 border-purple-500 hover:bg-purple-50'
                  }`}
                >
                  {t('common.ec')}
                </button>
              </>
            )}
          </div>

          {/* Date range preset buttons */}
          <div className="flex flex-wrap gap-2">
            {(['1d', '7d', '30d', '90d'] as DateRangePreset[]).map((preset) => (
              <button
                key={preset}
                onClick={() => handlePresetChange(preset)}
                className={`px-3 py-1 text-sm rounded border transition-colors ${
                  dateRangePreset === preset
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {preset === '1d' ? t('chart.hours24') : preset === '7d' ? t('chart.days7') : preset === '30d' ? t('chart.days30') : t('chart.days90')}
              </button>
            ))}
          </div>
        </div>

        {/* Custom date range */}
        <div className="mt-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('chart.startDate')}</label>
            <input
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('chart.endDate')}</label>
            <input
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <button
            onClick={handleCustomDateChange}
            disabled={!startDate || !endDate}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('chart.applyCustomRange')}
          </button>
          <button
            onClick={() => loadReadings()}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            {t('chart.refresh')}
          </button>
        </div>
      </div>

      {/* Chart container */}
      <div className="relative" style={{ height: '400px' }}>
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
            <div className="text-gray-600">{t('chart.loadingReadings')}</div>
          </div>
        ) : error ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-red-600">{error}</div>
          </div>
        ) : readings.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-gray-500">{t('chart.noReadingsForRange')}</div>
          </div>
        ) : (
          <Line data={chartData} options={chartOptions} />
        )}
      </div>

      {/* Reading count */}
      {!loading && readings.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 text-right">
          {t('chart.showingReading', { count: readings.length })}
          {dateRangePreset !== 'custom' && ` ${dateRangePreset === '1d' ? t('chart.fromLast24h') : dateRangePreset === '7d' ? t('chart.fromLast7d') : dateRangePreset === '30d' ? t('chart.fromLast30d') : t('chart.fromLast90d')}`}
        </div>
      )}
    </div>
  );
};

export default ReadingsChart;
